/**
 * What a failed sync is, and what a screen should draw because of it.
 *
 * This module exists because of one bug that cost a whole day. The sync engine
 * caught every failure, logged `console.warn`, and recorded nothing. The
 * loading gate in every mirror-reading hook was `rows.length === 0 &&
 * lastSyncedAt === null`, which cannot tell "the first sync is still in
 * flight" from "every sync since boot has failed". Two states collapsed into
 * one, and the collapsed state drew a spinner. A missing route on a stale
 * server therefore looked exactly like a slow network, forever.
 *
 * Three states, not two, is the whole fix. Everything here is pure so it can
 * be tested under bun — `sync-engine.ts` reaches `expo-sqlite` through the
 * repository and cannot load there, which is why the decisions live in this
 * file and not in the engine.
 */

/**
 * Why a sync run failed.
 *
 *   unreachable  no answer at all. On an offline-first app this is normal:
 *                the mirror is still the truth and the person can still work.
 *   server       an answer, and it was a refusal. The person cannot fix it.
 *   app          neither. A bug in this app, and it must keep its own words
 *                rather than be dressed up as a network problem.
 */
export type SyncFailureKind = 'unreachable' | 'server' | 'app';

export type SyncFailure = {
  readonly kind: SyncFailureKind;
  /** The HTTP status, when there was one. */
  readonly status: number | null;
  /** The sentence to show. Composed by the engine from the existing vocabulary. */
  readonly message: string;
  /** When the failing run finished. */
  readonly at: string;
  /** Consecutive failed runs since the last success. Drives the backoff. */
  readonly attempts: number;
};

/* ------------------------------------------------------------- classifying */

/**
 * The error names a request gets when it never reached a server.
 *
 * `expo/fetch` rejects with a `FetchError`; an aborted request is a
 * `DOMException` named AbortError. A bare `TypeError` is deliberately absent:
 * this app calls `expo/fetch` by name, so a TypeError is a bug here, and
 * reading it as a network problem hides that bug behind advice to start a
 * server which is already running.
 */
export const TRANSPORT_NAMES: ReadonlySet<string> = new Set(['FetchError', 'AbortError']);

const TRANSPORT_PHRASES: readonly string[] = [
  'network request failed',
  'failed to fetch',
  'load failed',
  'could not connect',
  'connection refused',
  'offline',
  // iOS, through expo/fetch, when a connection drops mid-request. It reached
  // a phone as "fetch failed: UnexpectedException: The network connection
  // was lost." during a first download, and drew the red "failed" card over
  // a record whose rows simply had not arrived yet.
  'network connection was lost',
  'timed out',
  'not connected to the internet',
  // Android, through OkHttp, for the same drop.
  'unexpected end of stream',
  'connection reset',
  'software caused connection abort',
  // Android, through OkHttp, when nothing answers at the host, the name does
  // not resolve, or the socket waits too long: "fetch failed:
  // java.net.ConnectException: Failed to connect to localhost/127.0.0.1:4000"
  // reached the dashboard's banner and Join a workspace word for word.
  'failed to connect',
  'connectexception',
  'unknownhostexception',
  'unable to resolve host',
  'sockettimeoutexception',
];

/** True for a request that never got an answer. */
export const isTransportFailure = (failure: unknown): boolean => {
  if (!(failure instanceof Error)) return false;
  if (TRANSPORT_NAMES.has(failure.name)) return true;
  const said = failure.message.toLowerCase();
  return TRANSPORT_PHRASES.some((phrase) => said.includes(phrase));
};

/**
 * Sort one thrown value into a kind and a status.
 *
 * Structural, not `instanceof`: `ApiRequestError` lives in `api.ts`, which
 * imports `expo/fetch` and so cannot load under bun. Reading `name` and
 * `status` off the value keeps this module testable and costs nothing.
 */
export const classifySyncFailure = (
  thrown: unknown,
): { kind: SyncFailureKind; status: number | null } => {
  const held = typeof thrown === 'object' && thrown !== null ? (thrown as Record<string, unknown>) : {};
  const status = typeof held.status === 'number' && Number.isFinite(held.status) ? held.status : null;
  if (status !== null && status !== 0) return { kind: 'server', status };
  if (isTransportFailure(thrown)) return { kind: 'unreachable', status: null };
  return { kind: 'app', status: null };
};

/** The next failure record, counting consecutive runs. */
export const nextFailure = (
  held: SyncFailure | null,
  next: { kind: SyncFailureKind; status: number | null; message: string; at: string },
): SyncFailure => ({
  ...next,
  attempts: (held?.attempts ?? 0) + 1,
});

/* -------------------------------------------------------------- presenting */

/**
 * What a screen that reads the mirror should draw.
 *
 *   loading       nothing local yet, and no answer either way. Keep waiting.
 *   ready         there is something to draw. A failing sync is the banner's
 *                 business, not this screen's — a good mirror is still good.
 *   offline-empty nothing local, and the server cannot be reached. Not an
 *                 error: the first sync simply has not happened. Say so
 *                 plainly and offer to try again.
 *   failed        nothing local, and the server refused. The person cannot
 *                 fix it and deserves to be told rather than shown a spinner.
 */
export type MirrorPresentation = 'loading' | 'ready' | 'offline-empty' | 'failed';

export const presentMirror = (input: {
  readonly mirrorEmpty: boolean;
  readonly lastSyncedAt: string | null;
  readonly failure: SyncFailure | null;
}): MirrorPresentation => {
  // Anything local is worth drawing, whatever the network is doing. This is
  // the offline-first promise: the mirror is the truth, not the server.
  if (!input.mirrorEmpty) return 'ready';
  // Synced, and genuinely empty. A new record with no entries is ready.
  if (input.lastSyncedAt !== null) return 'ready';
  if (input.failure === null) return 'loading';
  return input.failure.kind === 'unreachable' ? 'offline-empty' : 'failed';
};

/* ----------------------------------------------------------------- backoff */

/** The scheduled sync interval, matching the one in SyncManager. */
export const SYNC_INTERVAL_MS = 45_000;

/** However bad it gets, try again at least this often. */
export const MAX_BACKOFF_MS = 8 * 60_000;

/**
 * Whether the 45 second tick should actually run.
 *
 * An unreachable server costs nothing to retry — the request fails at once —
 * so offline keeps the plain interval. A server that is refusing is different:
 * hammering a 500 every 45 seconds helps nobody, and hammering a 404 forever
 * is what let a stale process hide. So a refusal backs off, doubling to a cap.
 *
 * Only the SCHEDULED tick consults this. A foreground, a socket ping, a pull
 * after a save, and an explicit Try again all bypass it: intent always
 * retries at once, and a success clears the failure.
 */
export const shouldRunScheduledSync = (failure: SyncFailure | null, nowMs: number): boolean => {
  if (failure === null || failure.kind === 'unreachable') return true;
  const since = nowMs - Date.parse(failure.at);
  if (!Number.isFinite(since)) return true;
  const wait = Math.min(SYNC_INTERVAL_MS * 2 ** Math.max(0, failure.attempts - 1), MAX_BACKOFF_MS);
  return since >= wait;
};

/* --------------------------------------------------------------- the outbox */

/**
 * Whether a failed push may drop the op it was pushing.
 *
 * Only a 4xx does. A 4xx is the server REFUSING the write — the values are
 * wrong, the record is gone, the tenant is not this one — and no retry mends
 * any of those, so the op and its ghost go, with a reason. A 5xx is the
 * server FAILING, and so is no answer at all: the write may well have landed,
 * and it certainly may land next time. Dropping there deletes an entry a
 * person typed and the server never refused.
 */
export const mayDropOp = (thrown: unknown): boolean => {
  const { status } = classifySyncFailure(thrown);
  return status !== null && status >= 400 && status < 500;
};

/* ------------------------------------------------------------- identity */

/**
 * Whether a failed `/v1/me` may fall back to the identity this device already
 * has.
 *
 * Only two answers genuinely revoke a person: 401, the session is no longer
 * good, and 403, the account may not read this. Everything else — no answer at
 * all, a missing route, a 500 — says nothing about who they are, and must not
 * log them out of an app whose whole promise is that it works without a
 * server. A stale server used to blank a device holding a year of entries.
 */
export const mayTrustCachedIdentity = (thrown: unknown): boolean => {
  const { status } = classifySyncFailure(thrown);
  return status !== 401 && status !== 403;
};

/* -------------------------------------------------------------- the banner */

/**
 * Whether a record or home screen with usable data should warn about sync.
 *
 * Never for `unreachable`: a working mirror with a dead network is a normal
 * state, and a banner there would train people to ignore banners. A 4xx shows
 * at once — a deterministic refusal does not heal on retry. A 5xx or an app
 * bug waits for a second failure, so one blip stays quiet.
 */
export const shouldWarnAboutSync = (failure: SyncFailure | null): boolean => {
  if (failure === null || failure.kind === 'unreachable') return false;
  if (failure.status !== null && failure.status >= 400 && failure.status < 500) return true;
  return failure.attempts >= 2;
};
